/**
 * Declarative PubNub channel-subscription ownership (realtime-layer ADR,
 * phase 2b).
 *
 * Consumers declare the channels they need under an owner key; the manager
 * reconciles the union of all owners against what is actually subscribed —
 * subscribing what's missing and unsubscribing what nobody wants anymore.
 * Room switch, room rename, and activity mount/unmount all become the same
 * operation ("declare what you need now"), which kills the class of bug
 * where each call site hand-rolled its own subscribe/unsubscribe pair.
 *
 * Reconnecting is mostly the SDK's job: with `restore: true` it resumes every
 * subscription in place when the network comes back (no leave+join flap).
 * Its subscribe retries give up after a few minutes, though, so after an
 * outage the browser never reported (a proxy or PubNub itself failing) the
 * manager keeps calling reconnect() with backoff until it connects. Once the
 * connection is back, the manager re-seeds presence for withPresence channels
 * (events during the gap are gone — hereNow is the recovery).
 */
import PubNub from 'pubnub';

import { realtimeLog } from './debug';
import { refreshPresence, releasePresenceStore } from './presenceBridge';

export interface DesiredChannel {
  channel: string;
  // Subscribe to the channel and its presence events (makes this client an
  // occupant of the channel).
  withPresence?: boolean;
  // Receive the channel's presence events only, by subscribing to its
  // `-pnpres` channel: this client is not an occupant and gets none of the
  // channel's messages. For a teacher watching the student channel.
  presenceOnly?: boolean;
}

const PRESENCE_SUFFIX = '-pnpres';

const owners = new Map<string, DesiredChannel[]>();
const subscribed = new Map<string, boolean>(); // subscription → withPresence

// The channel whose presence a subscription mirrors: the channel itself, or
// the parent of a `-pnpres` subscription.
const presenceChannelOf = (subscription: string) =>
  subscription.endsWith(PRESENCE_SUFFIX)
    ? subscription.slice(0, -PRESENCE_SUFFIX.length)
    : subscription;

let attachedTo: PubNub | null = null;
// Whether the SDK has connected since attach. Until then, the connect status
// seeds every presence channel; after it, a newly added channel is seeded
// when it's subscribed.
let connected = false;
let attachedListener: {
  status: (event: PubNub.StatusEvent) => void;
} | null = null;

// Retrying after the SDK's own subscribe retries are exhausted. Paused while
// the browser reports the network down: its `online` event reconnects.
const RECONNECT_BASE_DELAY_MS = 5000;
const RECONNECT_MAX_DELAY_MS = 60000;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
let networkDown = false;

function cancelReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function resetReconnect() {
  cancelReconnect();
  reconnectAttempts = 0;
  networkDown = false;
}

function scheduleReconnect() {
  if (networkDown || reconnectTimer || !attachedTo) {
    return;
  }
  const delay = Math.min(
    RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempts,
    RECONNECT_MAX_DELAY_MS
  );
  reconnectAttempts += 1;
  realtimeLog(`manager: subscribe gave up — reconnecting in ${delay}ms`);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    attachedTo?.reconnect();
  }, delay);
}

function desiredChannels(): Map<string, boolean> {
  const desired = new Map<string, boolean>();
  owners.forEach((channels) => {
    channels.forEach(({ channel, withPresence, presenceOnly }) => {
      if (presenceOnly) {
        // Plain subscribe to the presence channel; PubNub has no presence of
        // its own for `-pnpres` channels.
        const presence = `${channel}${PRESENCE_SUFFIX}`;
        desired.set(presence, desired.get(presence) ?? false);
        return;
      }
      desired.set(channel, (desired.get(channel) ?? false) || !!withPresence);
    });
  });
  return desired;
}

const { CATEGORIES } = PubNub;

function handleStatus(event: PubNub.StatusEvent) {
  if (event.category === CATEGORIES.PNNetworkDownCategory) {
    networkDown = true;
    cancelReconnect();
    return;
  }

  if (event.category === CATEGORIES.PNNetworkUpCategory) {
    // The SDK reconnects on its own right after emitting this; calling
    // reconnect() too can repeat the handshake and the presence re-seed
    // (a billable hereNow per channel) below.
    networkDown = false;
    realtimeLog('manager: network up — SDK is reconnecting');
    return;
  }

  // The handshake or the receive loop failed after the SDK's last retry, and
  // it waits in a failed state until something calls reconnect().
  if (
    event.category === CATEGORIES.PNConnectionErrorCategory ||
    event.category === CATEGORIES.PNDisconnectedUnexpectedlyCategory
  ) {
    scheduleReconnect();
    return;
  }

  // The event engine reports a restored connection as PNConnectedCategory;
  // PNReconnectedCategory is the pre-event-engine name, kept for safety.
  if (
    event.category === CATEGORIES.PNReconnectedCategory ||
    event.category === CATEGORIES.PNConnectedCategory
  ) {
    // The first connect seeds every presence channel; after an outage the
    // presence events are lost, and hereNow is the recovery. This is the only
    // place a presence channel is (re)seeded on connect, so every presence
    // consumer (grid, header) reads the result from the presence store
    // instead of calling hereNow itself.
    connected = true;
    cancelReconnect();
    reconnectAttempts = 0;
    realtimeLog(`manager: ${event.category} — seeding presence`);
    subscribed.forEach((withPresence, subscription) => {
      if (withPresence || subscription.endsWith(PRESENCE_SUFFIX)) {
        void refreshPresence(presenceChannelOf(subscription));
      }
    });
  }
}

/**
 * Attach to the current window.pubnub instance (the alias realtimeClient
 * assigns at init). If the instance is ever recreated, on re-attach the
 * subscribed-state cache is discarded and the desired set re-asserted.
 */
function ensureAttached(): boolean {
  const pubnub = window.pubnub;
  if (!pubnub) {
    return false;
  }
  if (attachedTo === pubnub) {
    return true;
  }
  if (attachedTo && attachedListener) {
    attachedTo.removeListener(attachedListener);
  }
  attachedListener = { status: handleStatus };
  pubnub.addListener(attachedListener);
  attachedTo = pubnub;
  connected = false;
  resetReconnect();
  subscribed.clear(); // new instance has no subscriptions yet
  realtimeLog('manager: attached to PubNub instance');
  return true;
}

function reconcile() {
  if (!ensureAttached() || !attachedTo) {
    return;
  }

  const desired = desiredChannels();

  const toSubscribe: string[] = [];
  const toSubscribeWithPresence: string[] = [];
  desired.forEach((withPresence, channel) => {
    const current = subscribed.get(channel);
    if (current === undefined || (!current && withPresence)) {
      // Not subscribed, or subscribed without presence but presence is now
      // wanted: (re)subscribe. Subscribing an already-subscribed channel
      // with withPresence adds its presence channel to the same subscription
      // loop, so no unsubscribe (and so no leave event) is needed.
      (withPresence ? toSubscribeWithPresence : toSubscribe).push(channel);
    }
  });

  const toUnsubscribe: string[] = [];
  subscribed.forEach((_withPresence, channel) => {
    if (!desired.has(channel)) {
      toUnsubscribe.push(channel);
    }
  });

  // Add before removing. Removing a room's channels first can leave the SDK
  // with none for a moment; it then disconnects and the new subscribe starts
  // a fresh handshake, whose connect status re-seeds every presence channel.
  if (toSubscribe.length > 0) {
    realtimeLog('manager: subscribing', toSubscribe);
    attachedTo.subscribe({ channels: toSubscribe });
    toSubscribe.forEach((channel) => subscribed.set(channel, false));
  }
  if (toSubscribeWithPresence.length > 0) {
    realtimeLog('manager: subscribing (withPresence)', toSubscribeWithPresence);
    attachedTo.subscribe({
      channels: toSubscribeWithPresence,
      withPresence: true,
    });
    toSubscribeWithPresence.forEach((channel) => subscribed.set(channel, true));
  }
  if (toUnsubscribe.length > 0) {
    realtimeLog('manager: unsubscribing', toUnsubscribe);
    attachedTo.unsubscribe({ channels: toUnsubscribe });
    toUnsubscribe.forEach((channel) => {
      subscribed.delete(channel);
      // No presence events will arrive for this channel anymore; drop its
      // store so room switches don't accumulate one store per room visited.
      releasePresenceStore(presenceChannelOf(channel));
    });
  }

  // A presence channel added after connect (room switch, rename, a live
  // activity mounting) gets no connect status of its own, so seed it now.
  // Before the first connect, the connect status seeds everything.
  if (connected) {
    [...toSubscribe, ...toSubscribeWithPresence].forEach((subscription) => {
      if (
        subscribed.get(subscription) ||
        subscription.endsWith(PRESENCE_SUFFIX)
      ) {
        void refreshPresence(presenceChannelOf(subscription));
      }
    });
  }
}

/**
 * Whether the manager currently receives presence for `channel` (a
 * withPresence subscription or a presence-only `-pnpres` one).
 */
export function hasPresenceSubscription(channel: string): boolean {
  return (
    subscribed.get(channel) === true ||
    subscribed.has(`${channel}${PRESENCE_SUFFIX}`)
  );
}

/** Declare the full channel set an owner needs right now. */
export function setChannels(owner: string, channels: DesiredChannel[]) {
  owners.set(owner, channels);
  reconcile();
}

/** Drop everything an owner declared. */
export function releaseChannels(owner: string) {
  owners.delete(owner);
  reconcile();
}

/**
 * The teacher's active room — exactly one at a time. Switching rooms (or
 * renaming one) is just a re-declaration; the reconcile handles the
 * unsubscribe of the old channels and the subscribe of the new.
 */
export function setActiveRoom(roomName: string | null) {
  const name = roomName?.toLowerCase();
  setChannels(
    'active-room',
    name
      ? [
          { channel: `${name}-teacher` },
          // Presence only: the teacher needs students' presence on the
          // student channel, not its messages (everything the teacher app
          // acts on is also published to -teacher), and shouldn't count as
          // an occupant of it.
          { channel: `${name}-student`, presenceOnly: true },
        ]
      : []
  );
}

/** Test/teardown hook. */
export function resetChannelManager() {
  if (attachedTo && attachedListener) {
    attachedTo.removeListener(attachedListener);
  }
  attachedTo = null;
  attachedListener = null;
  connected = false;
  resetReconnect();
  owners.clear();
  subscribed.clear();
}
